import { useTranslation } from 'react-i18next';

import type { PlanName } from '../../../api/types';
import { Badge } from '../../../shared/ui/Badge';
import { Button } from '../../../shared/ui/Button';
import styles from './PlanCard.module.css';

interface PlanCardProps {
  plan: PlanName;
  priceStars: number;
  periodDays: number;
  isCurrent: boolean;
  /** Правило AC-3 и его исключение решает экран, карточка только рисует. */
  showCta: boolean;
  ctaDisabled: boolean;
  onSelect: () => void;
}

/** Выгоды приходят из словаря (AC-3a) — backend маркетинговых текстов не отдаёт.
 * Число пунктов на тариф обязано совпадать во всех 4 локалях (localeParity). */
function readBenefits(value: unknown): string[] {
  if (typeof value !== 'object' || value === null) {
    return [];
  }
  return Object.values(value as Record<string, unknown>).filter(
    (benefit): benefit is string => typeof benefit === 'string',
  );
}

// PARA-131 (AC-3, AC-3a) — карточка тарифа. Цена, валюта и период берутся из
// ответа GET /billing/plans и во фронте не хардкодятся.
export function PlanCard({
  plan,
  priceStars,
  periodDays,
  isCurrent,
  showCta,
  ctaDisabled,
  onSelect,
}: PlanCardProps): JSX.Element {
  const { t } = useTranslation('billing');
  const benefits = readBenefits(t(`plans.${plan}.benefits`, { returnObjects: true }));

  return (
    <section className={styles.card} data-testid={`plan-card-${plan}`}>
      <div className={styles.head}>
        <h3 className={styles.name}>{t(`plans.${plan}.name`)}</h3>
        {isCurrent && <Badge>{t('plans.current')}</Badge>}
      </div>
      <p className={styles.price}>{t('plans.price', { price: priceStars, days: periodDays })}</p>
      <ul className={styles.benefits}>
        {benefits.map((benefit) => (
          <li key={benefit} className={styles.benefit}>
            {benefit}
          </li>
        ))}
      </ul>
      {showCta && (
        <Button fullWidth disabled={ctaDisabled} onClick={onSelect}>
          {t('plans.cta')}
        </Button>
      )}
    </section>
  );
}
